// BOORO: ghost avatars replace the shipped mascots on web and desktop.
import type { GhostSpec, GhostTier } from "@booro/ghost-avatar";
import { ghostBodyColor, ghostSeed, ghostSvg, resolveGhostSpec } from "@booro/ghost-avatar";
import "@booro/ghost-avatar/ghost-avatar.css";
import type { BotColorDef } from "@rakazo/core";
import { resolvePersonaColorDef } from "@rakazo/core";
import { memo, useId, useMemo, useSyncExternalStore } from "react";
import { cn } from "./lib/utils.js";

export interface BotGhost {
  spec: GhostSpec;
  colorDef: BotColorDef;
  seed: number;
}

/** Any stored non-photo avatar value as a ghost: dressed values as saved, older ones dressed at random. */
export function resolveBotGhost(
  value: string,
  identity: string,
  legacyShapeIndex?: number,
): BotGhost {
  const spec = resolveGhostSpec(value, identity, { shapeIndex: legacyShapeIndex });
  return {
    spec,
    colorDef: resolvePersonaColorDef(identity, ghostBodyColor(value, spec)),
    seed: ghostSeed(identity),
  };
}

export const GhostAvatar = memo(function GhostAvatar({
  spec,
  colorDef,
  seed,
  size,
  state = "idle",
  tier,
  className,
}: BotGhost & {
  size: number;
  state?: "idle" | "working" | "done";
  tier?: GhostTier;
  className?: string;
}) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const reducedMotion = useSyncExternalStore(
    subscribeToReducedMotion,
    reducedMotionSnapshot,
    () => false,
  );
  const svg = useMemo(
    () => ghostSvg({ spec, colors: colorDef, size, uid, seed, tier, reducedMotion }),
    [spec, colorDef, size, uid, seed, tier, reducedMotion],
  );
  return (
    <span
      className={cn(
        "rakazo-bot-avatar booro-ghost-avatar relative inline-flex shrink-0 select-none",
        className,
      )}
      data-working={state === "working"}
      data-ghost-state={state}
      style={{ width: size, height: size }}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: ghostSvg only interpolates validated enums, numbers and hex colors.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
});

const reducedMotionMedia = "(prefers-reduced-motion: reduce)";

function reducedMotionSnapshot(): boolean {
  return window.matchMedia(reducedMotionMedia).matches;
}

function subscribeToReducedMotion(onChange: () => void): () => void {
  const media = window.matchMedia(reducedMotionMedia);
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}
